import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAlbumParam } from "@/lib/slugify";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { PageHero } from "@/components/PageHero";

import { ProBadge } from "@/components/ui/pro-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { listMarketplace } from "@/lib/marketplace.functions";
import { startConversation } from "@/lib/chat.functions";
import { openChatWindow } from "@/components/chat/ChatDock";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMarketplaceEnabled } from "@/lib/i18n";
import {
  MapPin,
  MessageCircle,
  Filter,
  Search,
  Repeat2,
  Tag,
  LayoutGrid,
} from "lucide-react";

import { EmptyState } from "@/components/ui/empty-state";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";

const CONDITION_LABELS: Record<string, string> = {
  M: "Mint — como novo",
  NM: "Near Mint — quase perfeito",
  "VG+": "Very Good Plus — ótimo estado",
  VG: "Very Good — bom estado",
  G: "Good — estado regular",
  F: "Fair — desgastado",
  P: "Poor — ruim",
};

type ListingFilter = "all" | "for_sale" | "for_trade";

export const Route = createFileRoute("/marketplace")({
  validateSearch: (search: Record<string, unknown>): { tab?: ListingFilter } => {
    const t = search['tab'];
    return t === "for_sale" || t === "for_trade" ? { tab: t } : {};
  },
  head: () => ({ meta: [{ title: "Marketplace — Viny Club" }] }),
  component: Marketplace,
});

function Marketplace() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  // Marketplace é exclusivo do público brasileiro.
  const marketplaceEnabled = useMarketplaceEnabled();
  useEffect(() => {
    if (!marketplaceEnabled) navigate({ to: "/explore", replace: true });
  }, [marketplaceEnabled, navigate]);
  const fetchList = useServerFn(listMarketplace);
  const startConv = useServerFn(startConversation);

  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState<"recent" | "price_asc" | "price_desc">("recent");
  const [showFilters, setShowFilters] = useState(false);
  const { tab: tabParam } = Route.useSearch();
  const listingFilter: ListingFilter = tabParam ?? "all";

  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["marketplace", q, genre, maxPrice, sort],
    queryFn: () =>
      fetchList({
        data: {
          q: q || undefined,
          genre: genre || undefined,
          maxPrice: maxPrice ? Number(maxPrice) : undefined,
          sort,
        },
      }),
  });


  const visible = useMemo(() => {
    if (listingFilter === "all") return listings;
    return listings.filter((l) => l.listing === listingFilter);
  }, [listings, listingFilter]);

  const contact = useMutation({
    mutationFn: (otherUserId: string) => startConv({ data: { otherUserId } }),
    onSuccess: ({ conversationId }) => openChatWindow(conversationId),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <AppShell>
      <PageHero
        eyebrow="Comunidade"
        title="Marketplace"
        subtitle="Discos reais, colecionadores de verdade. Sem intermediário."
      />


      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 lg:px-5 lg:py-10">
        {/* Abas do marketplace, abaixo do título */}
        <div className="mb-5">
          <SegmentedTabs<ListingFilter>
            layoutId="marketplace-tabs"
            ariaLabel="Abas do marketplace"
            items={[
              { value: "all", label: "Tudo", icon: LayoutGrid },
              { value: "for_sale", label: "À venda", icon: Tag },
              { value: "for_trade", label: "Troca", icon: Repeat2 },
            ]}
            value={listingFilter}
            onChange={(v) =>
              navigate({
                to: "/marketplace",
                search: v === "all" ? {} : { tab: v },
                replace: true,
              })
            }
          />
        </div>

        {/* Busca + sort */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar artista ou álbum…"
              className="h-10 w-full rounded-full border border-input bg-surface pl-10 pr-4 text-base sm:text-sm"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as typeof sort)}
            className="h-10 rounded-full border border-border bg-surface px-4 text-sm"
          >
            <option value="recent">Mais recentes</option>
            <option value="price_asc">Menor preço</option>
            <option value="price_desc">Maior preço</option>
          </select>
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 text-sm"
          >
            <Filter className="h-3.5 w-3.5" /> Filtros
          </button>
        </div>

        {showFilters && (
          <div className="mb-6 flex flex-col gap-3 sm:flex-row">
            <input
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              placeholder="Gênero (ex: Jazz)"
              className="h-10 rounded-full border border-input bg-surface px-4 text-base sm:text-sm"
            />
            <input
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="Preço máx (R$)"
              className="h-10 rounded-full border border-input bg-surface px-4 text-base sm:w-40 sm:text-sm"
            />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && visible.length === 0 && (
          <div className="mx-auto max-w-xl">
            <EmptyState
              title="Nenhum anúncio encontrado"
              description="Ajuste os filtros ou use a sua coleção para anunciar o primeiro disco."
              icons={[<Tag key="t" />, <Repeat2 key="r" />, <LayoutGrid key="g" />]}
              action={{ label: "Ir para minha coleção", href: "/collection" }}
            />
          </div>
        )}

        {/* Loading skeletons */}
        {isLoading && (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] w-full animate-pulse rounded-lg bg-[#1A1A1A]"
              />
            ))}
          </div>
        )}

        {/* Grid de cards (mesmo estilo do explorar) */}
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {visible.map((l, i) => (
            <motion.div
              key={l.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: Math.min(i * 0.025, 0.25) }}
              className="group relative flex h-full w-full flex-col rounded-lg border border-border/40 bg-[#1A1A1A] p-3 transition-colors hover:border-border"
            >
              <div className="relative w-full overflow-hidden rounded-lg bg-muted/40">
                <LinkOrDiv discogsId={l.discogs_id} artist={l.artist} title={l.title} className="relative block w-full">
                  <div className="relative aspect-square w-full overflow-hidden rounded-lg">
                    {l.cover_url ? (
                      <img
                        src={l.cover_url}
                        alt={`${l.title} — ${l.artist}`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="h-full w-full bg-muted" />
                    )}
                    {/* Badge tipo */}
                    <div
                      className={
                        "absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-md backdrop-blur " +
                        (l.listing === "for_trade" ? "bg-[#9231FF]" : "bg-emerald-500")
                      }
                    >
                      {l.listing === "for_trade" ? (
                        <>
                          <Repeat2 className="h-2.5 w-2.5" /> Troca
                        </>
                      ) : (
                        <>
                          <Tag className="h-2.5 w-2.5" /> Venda
                        </>
                      )}
                    </div>
                    {l.sale_price != null && (
                      <div className="absolute right-2 top-2 rounded-full bg-black/75 px-2.5 py-1 text-xs font-bold text-white shadow-lg backdrop-blur">
                        R$ {l.sale_price}
                      </div>
                    )}
                    {l.condition && (
                      <div className="absolute bottom-2 left-2">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur">
                                {l.condition}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{CONDITION_LABELS[l.condition] || l.condition}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                </LinkOrDiv>
              </div>

              {/* Seller */}
              {l.seller && (
                <div className="mt-3 flex items-center gap-2">
                  <Link
                    to="/profile/$username"
                    params={{ username: l.seller.username }}
                    className="flex min-w-0 flex-1 items-center gap-2 text-[12px] text-muted-foreground hover:text-foreground"
                  >
                    <img loading="lazy" decoding="async"
                      src={
                        l.seller.avatar_url ||
                        `https://api.dicebear.com/7.x/initials/svg?seed=${l.seller.username}`
                      }
                      alt=""
                      className="h-7 w-7 rounded-full bg-muted object-cover"
                      style={
                        l.seller.is_premium
                          ? { border: "1.5px solid #9231FF", boxSizing: "border-box" }
                          : undefined
                      }
                    />
                    <span className="truncate">
                      {l.seller.display_name || l.seller.username}
                    </span>
                    {l.seller.is_premium && <ProBadge size="xs" aria-label="Premium" />}
                  </Link>
                </div>
              )}

              {/* Título */}
              <LinkOrDiv discogsId={l.discogs_id} artist={l.artist} title={l.title} className="mt-2 block">
                <div className="line-clamp-2 text-[13px] font-semibold leading-snug text-foreground">
                  {l.title}
                </div>
                <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                  {l.artist}
                  {l.year ? ` · ${l.year}` : ""}
                </div>
              </LinkOrDiv>

              {/* Footer */}
              {l.seller && (
                <div className="mt-3 flex items-center justify-between gap-2 border-t border-border/40 pt-2.5">
                  {l.seller.city ? (
                    <div className="flex min-w-0 items-center gap-1 truncate text-[11px] text-muted-foreground">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{l.seller.city}</span>
                    </div>
                  ) : (
                    <span />
                  )}
                  <button
                    onClick={() =>
                      user ? contact.mutate(l.seller!.id) : navigate({ to: "/auth" })
                    }
                    className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground transition-opacity hover:opacity-90"
                  >
                    <MessageCircle className="h-3 w-3" /> Contato
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}


function LinkOrDiv({
  discogsId,
  artist,
  title,
  className,
  children,
}: {
  discogsId: string | null;
  artist?: string | null;
  title?: string | null;
  className?: string;
  children: React.ReactNode;
}) {
  if (discogsId) {
    return (
      <Link to="/album/$id" params={{ id: buildAlbumParam(discogsId, artist, title) }} className={className}>
        {children}
      </Link>
    );
  }
  return <div className={className}>{children}</div>;
}
